import React, { useState } from 'react';
import { Category, Exam } from '../types';
import { generateCustomExam } from '../services/dataService';

interface CustomGeneratorProps {
  categories: Category[];
  onStartExam: (exam: Exam) => void;
  onCancel: () => void;
}

const CustomGenerator: React.FC<CustomGeneratorProps> = ({ categories, onStartExam, onCancel }) => {
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string[]>([]);
  const [questionCount, setQuestionCount] = useState(10);
  const [isGenerating, setIsGenerating] = useState(false);

  const toggleSubject = (id: string) => {
    setSelectedSubjects(prev => 
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const toggleDifficulty = (diff: string) => {
    setSelectedDifficulty(prev => 
      prev.includes(diff) ? prev.filter(d => d !== diff) : [...prev, diff]
    );
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      // Small delay to simulate processing
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const exam = await generateCustomExam({
        subjects: selectedSubjects,
        difficulty: selectedDifficulty,
        count: questionCount
      });
      
      if (exam.questions.length === 0) {
        alert("No questions found matching your criteria. Please try broader filters.");
        setIsGenerating(false);
        return;
      }
      
      onStartExam(exam);
    } catch (error) {
      console.error(error);
      alert("Failed to generate test.");
      setIsGenerating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-8 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold mb-2"><i className="fas fa-magic mr-3"></i>Custom Test Generator</h1>
              <p className="text-purple-100">Create personalized mock tests from our premium question warehouse.</p>
            </div>
            <button onClick={onCancel} className="text-white/80 hover:text-white transition-colors">
              <i className="fas fa-times text-2xl"></i>
            </button>
          </div>
        </div>

        <div className="p-8 space-y-8">
          {/* Subjects */}
          <div>
            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4">1. Select Subjects</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => toggleSubject(cat.id)}
                  className={`p-3 rounded-lg border text-sm font-medium transition-all flex items-center justify-center
                    ${selectedSubjects.includes(cat.id) 
                      ? 'bg-purple-50 dark:bg-purple-950/20 border-purple-500 text-purple-700 dark:text-purple-300 ring-1 ring-purple-500' 
                      : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-purple-300 dark:hover:border-purple-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                >
                  <i className={`fas ${cat.icon} mr-2`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
            {selectedSubjects.length === 0 && <p className="text-xs text-slate-400 mt-2">* Leaving blank selects all subjects</p>}
          </div>

          {/* Difficulty */}
          <div>
            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-4">2. Difficulty Level</h3>
            <div className="flex space-x-4">
              {['Easy', 'Medium', 'Hard'].map(diff => (
                <button
                  key={diff}
                  onClick={() => toggleDifficulty(diff)}
                  className={`flex-1 py-3 rounded-lg border font-medium transition-all
                    ${selectedDifficulty.includes(diff) 
                      ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-500 text-blue-700 dark:text-blue-300 ring-1 ring-blue-500' 
                      : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                >
                  {diff}
                </button>
              ))}
            </div>
          </div>

          {/* Count Slider */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-slate-800 dark:text-white">3. Number of Questions</h3>
              <span className="text-2xl font-bold text-purple-600 dark:text-purple-400">{questionCount}</span>
            </div>
            <input 
              type="range" 
              min="5" 
              max="50" 
              step="5"
              value={questionCount}
              onChange={(e) => setQuestionCount(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
            <div className="flex justify-between text-xs text-slate-400 dark:text-slate-500 mt-2">
              <span>5</span>
              <span>25</span>
              <span>50</span>
            </div>
          </div>

          {/* Action */}
          <div className="pt-6 border-t border-slate-100 dark:border-slate-700 flex justify-end gap-4">
            <button 
              onClick={onCancel}
              className="px-6 py-3 text-slate-600 dark:text-slate-400 font-medium hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="px-8 py-3 bg-slate-900 dark:bg-purple-600 text-white font-bold rounded-lg hover:bg-slate-800 dark:hover:bg-purple-700 transition-all shadow-lg flex items-center"
            >
              {isGenerating ? (
                <><i className="fas fa-circle-notch fa-spin mr-2"></i> Generating...</>
              ) : (
                <><i className="fas fa-play mr-2"></i> Start Custom Test</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomGenerator;